import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { TopicRequestBuilder } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/utils/TopicRequestBuilder';
import { useBaseTopicRequestBuilder } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/utils/useBaseTopicRequestBuilder';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { useAppSelector } from 'src/utils/Hooks';
import { useRetailerId } from 'src/utils/Hooks/reduxSelectors';
import axios from 'axios';
import EntityTable from 'src/components/BeaconV2Components/EntityTableRefresh/EntityTable';
import MetricCell from 'src/components/BeaconV2Components/MetricCell/MetricCell';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { calculatePercentChange } from 'src/utils/app';
import {
  topicTrafficServerProxy,
  getEntityIdToNameMap,
  GetEntityMetadataCampaignRecord
} from '../../serverProxy/topicTrafficServerProxy';
import _capitalize from 'lodash/capitalize';
import { splitCamelCase } from 'src/utils/stringFormatting';
import { GetAdCampaignMetricsResponse, GetAdCampaignMetricsResponseRecord } from './types';
import { TOPIC_QUERY_KEYS } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Settings/topicQueryKeys';

const TABLE_CELL_FONT_STYLE = { fontSize: '14px' as const };

type CampaignMap = Map<string, { name: string; record: GetEntityMetadataCampaignRecord }>;

export function PaidSearchCampaignsGrid() {
  const { startWeek, endWeek } = useAppSelector((state) => state.mainTimePeriod);
  const { startWeek: comparisonStartWeek, endWeek: comparisonEndWeek } = useAppSelector(
    (state) => state.comparisonTimePeriod
  );
  const retailerId = useRetailerId();
  const baseRequest = useBaseTopicRequestBuilder();

  const adCampaignMetricsRequest = baseRequest
    .setRetailerId(+retailerId)
    .setSortField('adClicks')
    .setStartWeekId(startWeek)
    .setEndWeekId(endWeek)
    .build();

  const adCampaignMetricsComparisonRequest = new TopicRequestBuilder(adCampaignMetricsRequest)
    .setStartWeekId(comparisonStartWeek)
    .setEndWeekId(comparisonEndWeek)
    .build();

  const { data, isLoading } = useQuery({
    queryKey: [
      TOPIC_QUERY_KEYS.all,
      'PaidSearchTopicsCampaignsGrid',
      adCampaignMetricsRequest,
      adCampaignMetricsComparisonRequest
    ],
    queryFn: async () => {
      const endpoint = '/api/beacontopics/GetAdCampaignMetrics';
      const [{ data: response }, { data: comparisonResponse }] = await Promise.all([
        axios.post<GetAdCampaignMetricsResponse>(endpoint, adCampaignMetricsRequest),
        axios.post<GetAdCampaignMetricsResponse>(endpoint, adCampaignMetricsComparisonRequest)
      ]);

      const campaignIds = response.records.map((record) => record.campaignId);
      const campaigns = await topicTrafficServerProxy.getEntityMetadata({
        entityIds: campaignIds,
        entityType: 'adCampaign',
        retailerId: +retailerId
      });

      return {
        responses: [response, comparisonResponse],
        campaignIdToNameMap: getEntityIdToNameMap(campaigns) as CampaignMap
      };
    }
  });

  const [response, comparisonResponse] = data?.responses ?? [{ records: [] }, { records: [] }];
  const campaignIdToNameMap: CampaignMap = data?.campaignIdToNameMap ?? new Map();

  const comparisonCampaignToMetricMap = comparisonResponse.records.reduce((acc, record) => {
    acc.set(record.campaignId, record);
    return acc;
  }, new Map<string, GetAdCampaignMetricsResponseRecord>());

  return (
    <EntityTable
      columnDefs={[
        {
          headerName: 'Campaigns',
          valueFormatter: (_, row: GetAdCampaignMetricsResponseRecord) => {
            return (
              <Text
                truncateLines={2}
                title={campaignIdToNameMap.get(row.campaignId)?.name ?? row.campaignId}
                variant="body2"
                sx={{
                  maxWidth: '140px',
                  wordWrap: 'break-word',
                  ...TABLE_CELL_FONT_STYLE
                }}
              >
                {campaignIdToNameMap.get(row.campaignId)?.name ?? row.campaignId}
              </Text>
            );
          }
        },
        {
          headerName: 'Campaign\nType',
          valueFormatter: (_, row: GetAdCampaignMetricsResponseRecord) => {
            return (
              <Text
                variant="body2"
                sx={{
                  maxWidth: '80px',
                  wordWrap: 'break-word'
                }}
              >
                {splitCamelCase(campaignIdToNameMap.get(row.campaignId)?.record.campaignType ?? '')}
              </Text>
            );
          }
        },
        {
          headerName: 'Campaign\nStatus',
          valueFormatter: (_, row: GetAdCampaignMetricsResponseRecord) => {
            return (
              <Text variant="body2">{_capitalize(campaignIdToNameMap.get(row.campaignId)?.record.state ?? '')}</Text>
            );
          }
        },
        ...(
          [
            ['adClicks', 'Ad\nClicks', METRICTYPE.VOLUME],
            ['adSpend', 'Ad\nSpend', METRICTYPE.MONEY],
            ['adSales', 'Ad\nSales', METRICTYPE.MONEY],
            ['adUnitsSold', 'Ad Units\nSold', METRICTYPE.VOLUME],
            ['roas', 'ROAS', METRICTYPE.MONEY]
          ] as const
        ).map(([field, title, metricType]) => {
          return {
            headerName: title,
            field,
            valueFormatter: (value, row: GetAdCampaignMetricsResponseRecord) => {
              const comparisonValue = comparisonCampaignToMetricMap.get(row.campaignId)?.[field] ?? 0;

              return (
                <MetricCell
                  metricType={metricType}
                  value={value}
                  percentChange={calculatePercentChange(value, comparisonValue)}
                  isAdSpendMetric={field === 'adSpend'}
                  wrapperStyle={TABLE_CELL_FONT_STYLE}
                />
              );
            }
          };
        })
      ]}
      rowData={response.records}
      shouldModifyColumnDefs={false}
      isLoading={isLoading}
    />
  );
}
